import { useState, useMemo } from 'react';

import CustomDropdown from '@/components/CustomDropdown';
import InputField from '@/components/InputField';
import LoaderComponent from '@/components/LoaderComponent';
import ModalContainer from '@/components/modals/ModalContainer';
import FormGroupWrapper from '@/features/sampleDevelopment/components/SampleRequestModals/FormGroupWrapper';
import InputWrapper from '@/features/sampleDevelopment/components/SampleRequestModals/InputWrapper';
import ModalHeader from '@/components/modals/ModalHeader';
import ModalFooter from '@/features/sampleDevelopment/components/SampleRequestModals/ModalFooter';
import { useGetAllUserQuery, useGetEnumsQuery } from '@/api/commonApis';
import {
	useGetSampleInventoryQuery,
	useSubmitPhysicalSampleRequestMutation
} from '@/features/sampleDevelopment/api/sampleDevelopmentApi';
import CommonImage from '@/components/CommonImage';
import { packTag, setTag, getUserInfo } from '@/utils/Util';
import { toastError, toastSuccess } from '@/components/Toast';

const INITIAL_STATE = {
	reason: null,
	purposeDescription: '',
	sampleRecipient: null,
	willReturnAgain: null,
	requiredDate: ''
};

const PhysicalSampleRequestModal = ({ onClose, selectedDesigns = [], setSelectedProdutIds }) => {
	const [sampleInputData, setSampleInputData] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);
	const [sampleDetailsErrors, setSampleDetailsErrors] = useState([]);
	// style id -> reason returned by the backend for the styles that could not be requested
	const [requestErrors, setRequestErrors] = useState({});

	const { data: userList = [], isLoading: userLoading } = useGetAllUserQuery({
		params: { page: 0, size: 1000 }
	});

	// Sort user list: logged-in user first, then user with id 12561, then rest
	const sortedUserList = useMemo(() => {
		if (!userList || userList.length === 0) return [];

		const loggedInUser = getUserInfo();
		const loggedInUserId = loggedInUser?.id;
		const secondUserId = 12561;

		return [...userList].sort((a, b) => {
			// Logged-in user should be first
			if (a.id === loggedInUserId) return -1;
			if (b.id === loggedInUserId) return 1;

			// User with id 12561 should be second
			if (a.id === secondUserId) return -1;
			if (b.id === secondUserId) return 1;

			// Keep the rest as is
			return 0;
		});
	}, [userList]);

	const { data: sampleInventoryBd } = useGetSampleInventoryQuery({
		params: { productIds: selectedDesigns?.join(',') }
	});
	const { data: sampleReasonOptions, isLoading: isReasonLoading } = useGetEnumsQuery({
		path: 'info.nitex.app.sample.SampleInventoryRequestReason'
	});
	const { data: sampleRequiredWithinOptions, isLoading: isLoadingRequiredWithin } = useGetEnumsQuery({
		path: 'info.nitex.app.sample.InventorySampleRequiredWithin'
	});
	const [submitPhysicalSampleRequest, { isLoading: isSubmittingRequest }] =
		useSubmitPhysicalSampleRequestMutation();

	const handleChange = (name, value) => {
		setSampleInputData((prev) => ({
			...prev,
			[name]: value
		}));
		setErrors((prev) => ({
			...prev,
			[name]: ''
		}));
	};

	const handleRemoveProduct = (item) => {
		const updatedIDs = selectedDesigns?.filter((prodID) => prodID !== item?.id);
		setSelectedProdutIds && setSelectedProdutIds(updatedIDs);

		const updateErrorIds = sampleDetailsErrors.filter((prodId) => prodId !== item?.id);
		setSampleDetailsErrors(updateErrorIds);

		setRequestErrors((prev) => {
			const rest = { ...prev };
			delete rest[item?.id];
			return rest;
		});

		if (updatedIDs?.length === 0) {
			onClose();
		}
	};

	const isValidateForm = () => {
		const newErrors = {};
		// if (!sampleInputData?.reason) {
		// 	newErrors.reason = 'Please Select the reason.';
		// }
		if (!sampleInputData?.purposeDescription) {
			newErrors.purposeDescription = 'Please write the purpose';
		}
		if (!sampleInputData?.sampleRecipient) {
			newErrors.sampleRecipient = 'Please select the person';
		}
		if (!sampleInputData?.willReturnAgain) {
			newErrors.willReturnAgain = 'When will be returned?';
		}
		if (!sampleInputData?.requiredDate) {
			newErrors.requiredDate = 'Select required date';
		}
		setErrors(newErrors);
		return Object.keys(newErrors)?.length === 0;
	};

	const isValidateSampleItem = () => {
		const emptyItems = [];
		sampleInventoryBd?.forEach((sample) => {
			// if (sample?.productResponse?.isSet) {
			// 	// For sets, check if ALL set members have samples available
			// 	const allMembersHaveSamples = sample?.setMemberProducts?.every(
			// 		(member) =>
			// 			member?.sampleInventoryLocationResponseList &&
			// 			member?.sampleInventoryLocationResponseList?.length > 0
			// 	);
			// 	// If any set member doesn't have samples, add the set ID to empty items
			// 	if (!allMembersHaveSamples) {
			// 		emptyItems.push(sample?.productResponse?.id);
			// 	}
			// } else {
			// 	// For non-sets, check if the product has samples available
			// }
			if (
				!sample?.sampleInventoryLocationResponseList ||
				sample?.sampleInventoryLocationResponseList?.length === 0
			) {
				emptyItems.push(sample?.productResponse?.id);
			}
		});

		setSampleDetailsErrors(emptyItems);
		return emptyItems?.length === 0;
	};

	const handleSubmit = () => {
		const productHasSample = isValidateSampleItem();
		const hasFormData = isValidateForm();
		if (!hasFormData) {
			return;
		}
		if (!productHasSample) {
			toastError('Please select styles that have samples available in the BD.');
			return;
		}

		// Build productIdList: exclude sets and add their set members instead
		const productIdList = [];

		selectedDesigns?.forEach((productId) => {
			const inventoryItem = sampleInventoryBd?.find((item) => item?.productResponse?.id === productId);

			if (inventoryItem?.productResponse?.isSet) {
				// If it's a set, add its set member IDs instead of the set ID
				if (inventoryItem?.productResponse?.setMemberProductIds?.length > 0) {
					productIdList.push(...inventoryItem.productResponse.setMemberProductIds);
				}
			} else {
				// If it's not a set, add the product ID as is
				productIdList.push(productId);
			}
		});

		const payload = {
			reason: sampleInputData?.reason?.id,
			purpose: sampleInputData?.purposeDescription,
			willReturn: sampleInputData?.willReturnAgain?.id,
			requiredWithin: sampleInputData?.requiredDate?.id,
			recipientId: sampleInputData?.sampleRecipient?.id,
			productIdList: productIdList
		};

		submitPhysicalSampleRequest({ body: payload })
			.unwrap()
			.then(() => {
				toastSuccess('Request sent successfully');
				onClose();
			})
			.catch((error) => {
				const styleErrors = getStyleWiseErrors(error?.data?.returnObject);
				const errorStyleIds = Object.keys(styleErrors).map(Number);
				setRequestErrors(styleErrors);
				setSampleDetailsErrors(errorStyleIds);

				if (errorStyleIds.length > 1)
					toastError(
						`${errorStyleIds.length} styles can't be requested. Please check the highlighted styles.`
					);
				else toastError(error?.data?.message || 'Something went wrong');
			});
	};

	// backend returns errors by requested product id; sets are requested by their members, so map members back to the set row
	const getStyleWiseErrors = (returnObject) => {
		if (!returnObject || typeof returnObject !== 'object') return {};

		const styleErrors = {};
		Object.entries(returnObject).forEach(([productId, message]) => {
			const id = Number(productId);
			const inventoryItem = sampleInventoryBd?.find(
				(item) =>
					item?.productResponse?.id === id ||
					item?.productResponse?.setMemberProductIds?.includes(id)
			);
			const styleId = inventoryItem?.productResponse?.id ?? id;
			if (!styleErrors[styleId]) styleErrors[styleId] = message;
		});
		return styleErrors;
	};

	const renderRequestError = (styleId) =>
		requestErrors[styleId] && <p className="error regular-12 mt-1">{requestErrors[styleId]}</p>;

	const renderDesignImage = (item) => {
		if (item?.isSet) {
			return (
				<div className="d-flex align-items-center">
					<div className="set-image-layout" style={{ width: ' 56px' }}>
						<CommonImage
							src={item?.designDocuments?.length > 0 && item?.designDocuments[0]?.docUrl}
							alt="image"
							className="cell-img pr-2"
							isStatic={false}
						/>
						<CommonImage
							src={item?.designDocuments?.length > 1 && item?.designDocuments[1]?.docUrl}
							alt="image"
							className="cell-img pr-2"
							isStatic={false}
						/>
					</div>
					<div className="ml-12">
						{item?.isSet && setTag()}
						<p className="regular-12 gray_dark_01 py-1">{item?.name}</p>
						<ul className="common-list-component mb-2">
							<li>{item?.setMemberProducts && item?.setMemberProducts[0]?.productGroup}</li>
							<li>{item?.setMemberProducts && item?.setMemberProducts[0]?.category}</li>
							<li>{item?.setMemberProducts && item?.setMemberProducts[1]?.category}</li>
						</ul>
					</div>
					<div
						className="action-btn cursor-pointer position-absolute top-0 right-0 d-flex align-items-center justify-content-center"
						onClick={() => handleRemoveProduct(item)}
					>
						<CommonImage src="/icons/close-active.png" width={12} height={12} alt="edit" />
					</div>
				</div>
			);
		}

		return (
			<div className="d-flex align-items-center">
				<CommonImage
					src={item?.designDocuments?.length && item?.designDocuments[0]?.docUrl}
					alt="image"
					className="cell-img"
					isStatic={false}
				/>
				<div className="ml-2">
					{item?.isPack && packTag()}
					<p className="regular-12 gray_dark_01 py-1">{item.referenceNumber}</p>
					<ul className="common-list-component mb-2">
						<li>{item?.productGroup}</li>
						<li>{item?.category}</li>
					</ul>
				</div>
				<div
					className="action-btn cursor-pointer position-absolute top-0 right-0 d-flex align-items-center justify-content-center"
					onClick={() => handleRemoveProduct(item)}
				>
					<CommonImage src="/icons/close-active.png" width={12} height={12} alt="edit" />
				</div>
			</div>
		);
	};

	return (
		<LoaderComponent
			loading={userLoading || isSubmittingRequest || isReasonLoading || isLoadingRequiredWithin}
		>
			<ModalContainer className="sample-request-modal width-720-modal">
				<div className="common-popup sample-request-modal add-sample-details-popup shadow-16dp">
					<ModalHeader title="Request for BD inventory sample" onClose={onClose} />
					<div className="common-popup-body scroll-y-label">
						<div className="send-information-flied">
							{' '}
							<div className="flied-item d-grid grid-columns-2 gap-3">
								<FormGroupWrapper labelTitle="Why do you need these samples?">
									<CustomDropdown
										title="Select"
										selectedItem={sampleInputData?.reason?.value}
										items={sampleReasonOptions?.map((option) => ({
											...option,
											value: option?.name,
											id: option?.constant
										}))}
										onItemClick={(value) => handleChange('reason', value)}
									/>
								</FormGroupWrapper>

								<InputWrapper labelTitle="Explain the purpose*">
									<InputField
										type="text"
										name="purposeDescription"
										value={sampleInputData?.purposeDescription}
										onChangeHandler={(e) =>
											handleChange('purposeDescription', e.target.value)
										}
										withWrapper={false}
										withLabel={false}
										placeholder="Buyer name/Factory/Review reason/etc"
										errors={errors?.purposeDescription}
									/>
								</InputWrapper>
							</div>
							<div className="flied-item d-grid grid-columns-3 gap-3">
								<FormGroupWrapper labelTitle="Whom to send sample?*">
									<CustomDropdown
										title="Select"
										selectedItem={sampleInputData?.sampleRecipient?.name}
										items={sortedUserList?.map((user) => ({
											...user,
											value: user?.nickName
										}))}
										onItemClick={(value) => handleChange('sampleRecipient', value)}
									/>
									{errors?.sampleRecipient && (
										<p className="error">{errors?.sampleRecipient}</p>
									)}
								</FormGroupWrapper>

								<FormGroupWrapper labelTitle="Will you return it again?*">
									<CustomDropdown
										title="Select"
										selectedItem={sampleInputData?.willReturnAgain?.value}
										items={[
											{ id: true, value: 'Yes' },
											{ id: false, value: 'No' }
										]}
										onItemClick={(value) => handleChange('willReturnAgain', value)}
									/>
									{errors?.willReturnAgain && (
										<p className="error">{errors?.willReturnAgain}</p>
									)}
								</FormGroupWrapper>

								<FormGroupWrapper labelTitle="Required date*">
									<CustomDropdown
										title="Select"
										selectedItem={sampleInputData?.requiredDate?.name}
										items={sampleRequiredWithinOptions?.map((option) => ({
											...option,
											value: option?.name,
											id: option?.constant
										}))}
										onItemClick={(value) => handleChange('requiredDate', value)}
									/>
									{errors?.requiredDate && <p className="error">{errors?.requiredDate}</p>}
								</FormGroupWrapper>
							</div>
						</div>
						<div className="send-pd-table sample-details-table scroll-x-label">
							<table className="table with-border">
								<thead>
									<tr>
										<th className="design-details-column">
											<span className="ml-2">Design ({sampleInventoryBd?.length})</span>
										</th>
										<th className="sample-details-column">Currently available at BD</th>
									</tr>
								</thead>

								<tbody>
									{sampleInventoryBd?.map((item) => {
										const isSet = item?.productResponse?.isSet;
										const setMembers = item?.setMemberProducts || [];

										// For sets, render multiple rows (one per set member)
										if (isSet && setMembers.length > 0) {
											return setMembers.map((member, index) => (
												<tr
													key={`${item?.productResponse?.id}-${member?.id || index}`}
													className={
														sampleDetailsErrors.includes(
															item?.productResponse?.id
														)
															? 'row-error'
															: ''
													}
												>
													{/* Design column only in first row with rowSpan */}
													{index === 0 && (
														<td
															className="design-column"
															rowSpan={setMembers.length}
														>
															{renderDesignImage(item?.productResponse)}
															{renderRequestError(item?.productResponse?.id)}
														</td>
													)}

													{/* Sample location details for each set member */}
													<td>
														<div className="sample-details">
															{member?.sampleInventoryLocationResponseList
																?.length > 0 &&
																member?.sampleInventoryLocationResponseList?.map(
																	(sample) => (
																		<p key={sample?.id}>
																			{[
																				sample?.locationId,
																				sample?.countryResponse?.code,
																				sample?.buildingTagResponse
																					?.text,
																				sample?.floorTagResponse
																					?.text,
																				sample?.roomOrAreaTagResponse
																					?.text
																			]
																				.filter(Boolean)
																				.join(', ')}
																		</p>
																	)
																)}
														</div>
													</td>
												</tr>
											));
										}

										// For non-sets, render single row
										return (
											<tr
												key={item?.productResponse?.id}
												className={
													sampleDetailsErrors.includes(item?.productResponse?.id)
														? 'row-error'
														: ''
												}
											>
												<td className="design-column">
													{renderDesignImage(item?.productResponse)}
													{renderRequestError(item?.productResponse?.id)}
												</td>

												<td>
													<div className="sample-details">
														{item?.sampleInventoryLocationResponseList?.length >
															0 &&
															item?.sampleInventoryLocationResponseList?.map(
																(sample) => (
																	<p key={sample?.id}>
																		{[
																			sample?.locationId,
																			sample?.countryResponse?.code,
																			sample?.buildingTagResponse?.text,
																			sample?.floorTagResponse?.text,
																			sample?.roomOrAreaTagResponse
																				?.text
																		]
																			.filter(Boolean)
																			.join(', ')}
																	</p>
																)
															)}
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>

					<ModalFooter onSubmit={handleSubmit} disabled={isSubmittingRequest} />
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default PhysicalSampleRequestModal;
